import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";
import { BACKEND_URL } from "@/lib/api";
import { CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

const DECISION_LABELS = {
  released: "Release",
  released_with_comments: "Release with Comments",
  rejected: "Reject",
  rectification_required: "Request Rectification",
};

export default function PublicReleaseV2() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signatoryName, setSignatoryName] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [sig, setSig] = useState(null);
  const [decisions, setDecisions] = useState({}); // {rel_id: {decision, comments}}
  const [submittedIds, setSubmittedIds] = useState(new Set());

  const reload = () => {
    setLoading(true);
    publicApi.get(`/public/itp-v2/release/${token}`)
      .then((r) => { setData(r.data); setSignatoryName(r.data?.request?.recipient_name || ""); })
      .catch((e) => toast.error(e.response?.data?.detail || "Link invalid or expired"))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [token]);

  const submitOne = async (rel_id) => {
    const d = decisions[rel_id];
    if (!d || !d.decision) { toast.error("Choose a decision"); return; }
    if (!signatoryName.trim()) { toast.error("Enter your name"); return; }
    try {
      await publicApi.post(`/public/itp-v2/release/${token}/decision`, {
        release_req_id: rel_id, decision: d.decision, comments: d.comments || "",
        signatory_name: signatoryName.trim(), company: company.trim(), role: role.trim(),
        signature_image_b64: sig, signature_method: sig ? "drawn" : "typed",
      });
      setSubmittedIds((prev) => new Set([...prev, rel_id]));
      toast.success(`${DECISION_LABELS[d.decision]} recorded`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  if (!data) return <div className="p-6 text-sm text-rose-700" data-testid="public-rel-invalid">This link is not valid or has expired.</div>;

  const req = data.request;
  const items = data.items || [];
  const allDone = items.every((i) => submittedIds.has(i.id) || ["released", "released_with_comments", "rejected", "rectification_required", "cancelled"].includes(i.status));

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4 sm:p-6" data-testid="public-release-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">FlowQA — Release Request{data.is_bulk ? " (Bulk)" : ""}</h1>
        <div className="mt-1 text-sm text-slate-600">Review each item and record your decision. You may release, release with comments, reject, or request rectification for each item independently.</div>
      </div>
      <Card className="p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><Label>Your Name *</Label><Input value={signatoryName} onChange={(e) => setSignatoryName(e.target.value)} data-testid="public-rel-name" /></div>
          <div><Label>Company</Label><Input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="public-rel-company" /></div>
          <div><Label>Role / Title</Label><Input value={role} onChange={(e) => setRole(e.target.value)} data-testid="public-rel-role" /></div>
          <div className="sm:col-span-2">
            <Label>Signature (optional)</Label>
            <SignaturePad value={sig} onChange={setSig} />
          </div>
        </div>
      </Card>
      <Card className="overflow-hidden">
        <div className="border-b bg-slate-50 px-4 py-2 text-sm font-medium text-slate-700">Items ({items.length})</div>
        <div className="divide-y">
          {items.map((rel) => {
            const done = submittedIds.has(rel.id) || ["released", "released_with_comments", "rejected", "rectification_required", "cancelled"].includes(rel.status);
            const d = decisions[rel.id] || {};
            return (
              <div key={rel.id} className="p-3" data-testid={`public-rel-item-${rel.id}`}>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Badge variant="outline" className="border-teal-300 bg-teal-50 text-teal-800">{rel.point_type_code}</Badge>
                  <span>{rel.active_itp_summary?.lot_number} · {rel.active_itp_summary?.template_name}</span>
                </div>
                <div className="mt-1 text-sm text-slate-800"><span className="font-semibold">Activity:</span> {rel.activity || "—"}</div>
                {rel.acceptance_criteria && <div className="text-xs text-slate-600"><span className="font-semibold">Acceptance:</span> {rel.acceptance_criteria}</div>}
                {(rel.evidence || []).length > 0 && (
                  <div className="mt-1 text-xs text-slate-600">Evidence: {rel.evidence.map((e) => e.filename || e.original_name || e.id).join(", ")}</div>
                )}
                {done ? (
                  <div className="mt-2 inline-flex items-center gap-1 rounded bg-emerald-50 px-2 py-1 text-xs text-emerald-800" data-testid={`public-rel-done-${rel.id}`}>
                    <CheckCircle2 className="size-3" /> Decision recorded ({rel.status || "submitted"})
                  </div>
                ) : (
                  <div className="mt-2 space-y-2">
                    <div className="flex flex-wrap gap-1 text-xs">
                      {Object.entries(DECISION_LABELS).map(([k, v]) => (
                        <button
                          key={k}
                          onClick={() => setDecisions((prev) => ({ ...prev, [rel.id]: { ...(prev[rel.id] || {}), decision: k } }))}
                          className={`rounded border px-2 py-1 ${d.decision === k ? "border-teal-600 bg-teal-100 text-teal-900" : "border-slate-300 bg-white text-slate-700"}`}
                          data-testid={`public-rel-decision-${rel.id}-${k}`}
                        >{v}</button>
                      ))}
                    </div>
                    <Textarea rows={2} placeholder="Comments (optional; required for reject / rectification)"
                              value={d.comments || ""}
                              onChange={(e) => setDecisions((prev) => ({ ...prev, [rel.id]: { ...(prev[rel.id] || {}), comments: e.target.value } }))}
                              data-testid={`public-rel-comments-${rel.id}`} />
                    <Button size="sm" onClick={() => submitOne(rel.id)} disabled={!signatoryName.trim() || !d.decision}
                            data-testid={`public-rel-submit-${rel.id}`}>Submit Decision</Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
      {allDone && (
        <div className="rounded border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="public-rel-all-done">
          All items have been decided. Thank you.
        </div>
      )}
    </div>
  );
}
